import { useMemo, useState } from "react";
import { RefreshCw, Pencil, ChevronDown, ChevronRight, Mail, Phone } from "lucide-react";
import { PAYMENT_BADGE, PAYMENT_LABEL } from "./shared";

export default function CustomersTab({ ctx }) {
  const { customers, loading, refreshCustomers, setEditingCustomer } = ctx;
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return customers;
    return customers.filter((c) => {
      const athletes = (c.athletes || []).map((a) => a.name).join(" ");
      return `${c.parent_name} ${c.email} ${c.phone} ${athletes}`.toLowerCase().includes(s);
    });
  }, [customers, q]);

  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Customers</h1>
        <button data-testid="customers-refresh-btn" onClick={refreshCustomers} disabled={loading}
          className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-4 py-2.5 transition-colors">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="mt-3 text-sm font-medium text-white/50">Parent/customer accounts grouped by email, with their athletes and full enrollment history.</p>

      <input data-testid="customer-search" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search parent, athlete, email, phone…"
        className="mt-6 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#2563EB] transition-colors" />

      <p className="mt-5 text-xs font-bold uppercase tracking-wider text-white/40">{filtered.length} customer{filtered.length === 1 ? "" : "s"}</p>

      <div className="mt-3 space-y-3" data-testid="customers-list">
        {filtered.length === 0 && <p className="text-white/40 font-medium">No customers found.</p>}
        {filtered.map((c) => {
          const key = c.email || c.parent_name;
          const isOpen = !!open[key];
          return (
            <div key={key} data-testid={`customer-${key}`} className="rounded-2xl bg-white/5 border border-white/10 p-5">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <button onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))} className="flex items-center gap-3 text-left min-w-0">
                  {isOpen ? <ChevronDown size={18} className="text-white/40 shrink-0" /> : <ChevronRight size={18} className="text-white/40 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-lg font-bold text-white truncate">{c.parent_name || "—"}</p>
                    <p className="mt-0.5 text-sm font-medium text-white/50 flex flex-wrap items-center gap-x-3">
                      <span className="inline-flex items-center gap-1.5"><Mail size={12} /> {c.email || "no email"}</span>
                      <span className="inline-flex items-center gap-1.5"><Phone size={12} /> {c.phone || "no phone"}</span>
                    </p>
                    <p className="mt-0.5 text-xs font-medium text-white/40">{(c.athletes || []).length} athlete{(c.athletes || []).length === 1 ? "" : "s"} · {c.enrollment_count} enrollment{c.enrollment_count === 1 ? "" : "s"}</p>
                  </div>
                </button>
                <button data-testid={`edit-customer-${key}`} onClick={() => setEditingCustomer({ email: c.email, parent_name: c.parent_name, phone: c.phone, new_email: "" })}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-3.5 py-2 transition-colors shrink-0"><Pencil size={13} /> Edit</button>
              </div>

              {isOpen && (
                <div className="mt-5 border-t border-white/10 pt-5 space-y-5" data-testid={`customer-detail-${key}`}>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#2563EB]">Athletes</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {(c.athletes || []).map((a, i) => (
                        <span key={i} className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-sm font-semibold text-white">
                          {a.name}{a.age ? <span className="text-white/40 font-medium"> · {a.age}</span> : ""}{a.division ? <span className="text-white/40 font-medium"> · {a.division}</span> : ""}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#2563EB]">Enrollment History</p>
                    <div className="mt-2 space-y-2">
                      {(c.enrollments || []).map((e) => {
                        const badge = PAYMENT_BADGE[e.payment_status] || PAYMENT_BADGE.initiated;
                        return (
                          <div key={e.enrollment_key} className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                            <div>
                              <p className="text-white font-bold text-sm">{e.program_title} <span className="text-white/40 font-medium">· {e.athlete_name}</span></p>
                              <p className="text-white/40 text-xs mt-0.5">{[e.day, e.time].filter(Boolean).join(" ")}{(e.start_date || e.end_date) ? ` · ${[e.start_date, e.end_date].filter(Boolean).join("–")}` : ""} · ${e.amount}</p>
                            </div>
                            <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold uppercase ${badge}`}>{PAYMENT_LABEL[e.payment_status] || e.payment_status}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
